import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaFileExcel, FaTrash, FaEdit } from "react-icons/fa";
import { IoSchool } from "react-icons/io5";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import {
  importTeacherCodes,
  fetchTeachersCodes,
  deleteTeachersCodes,
  clearState,
} from "../../redux/Slices/TeachersCodesSlice";
import { getAllTeachers } from "../../redux/Slices/TeachersSlice";
import { fetchSettingsGrades } from "../../redux/Slices/GradesSlice";

import { useTitle } from "../../context/TitleContext";
import PermissionMotionButton from "../PermissionMotionButton";
import PermissionButton from "../PermissionButton";
import Pagination from '../../components/Pagination';

const TeacherCodes = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [filterTeacher, setFilterTeacher] = useState("");
  const [filterGrade, setFilterGrade] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [download, setDownload] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  const [formValues, setFormValues] = useState({
    teacher_id: "",
    price: "",
    grade_id: "",
    file: null,
  });

  const { teachers_codes, meta, isLoading, error, success } = useSelector(
    (state) => state.teachers_codes
  );

  const { all_teachers } = useSelector((state) => state.teachers);
  const { settingsGrades } = useSelector((state) => state.grades);

  useEffect(() => {
    setTitle("أكواد المعلمين");
  }, [setTitle]);

  const handleDownloadExcel = async () => {
    try {
      setDownload(true);
      const getAuthToken = () => {
        try {
          const localData = localStorage.getItem("loggedUser");
          if (localData) {
            const parsed = JSON.parse(localData);
            if (parsed.token) return parsed.token;
            if (parsed.data?.token) return parsed.data.token;
            if (parsed.result?.token) return parsed.result.token;
            if (parsed.user?.token) return parsed.user.token;
          }
          return null;
        } catch {
          return null;
        }
      };

      const token = getAuthToken();
      if (!token) {
        toast.error("خطأ في المصادقة، يرجى تسجيل الدخول مجدداً");
        setDownload(false);
        return;
      }

      const BASE_URL = process.env.REACT_APP_BASE_URL || "https://backend.mystore.education/api";
      const response = await fetch(
        `${BASE_URL}/dashboard/teachers-codes/download-template`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("فشل التحميل");
      }

      const data = await response.json();

      if (data?.status && data?.data?.url) {
        setTimeout(() => {
          window.location.href = data.data.url;
        }, 300);
      } else {
        throw new Error("رابط التحميل غير متاح");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("حدث خطأ أثناء تحميل الملف");
    } finally {
      setDownload(false);
    }
  };
  
  useEffect(() => {
    dispatch(getAllTeachers());
    dispatch(fetchSettingsGrades());
  }, [dispatch]);

  useEffect(() => {
    dispatch(clearState());
    dispatch(fetchTeachersCodes({ page: currentPage, teacher_id: filterTeacher, grade_id: filterGrade }));
  }, [dispatch, currentPage, filterTeacher, filterGrade]);

  useEffect(() => {
    if (success) {
      setShowDeleteModal(false);
      setDeleting(null);
      if (showModal) {
        setShowModal(false);
        setFormValues({
          teacher_id: "",
          price: "",
          grade_id: "",
          file: null,
        });
      }
      setTimeout(() => {
        toast.success(success, { position: "top-center", zIndex: 9999 });
      }, 100);
      dispatch(fetchTeachersCodes({ page: currentPage, teacher_id: filterTeacher, grade_id: filterGrade }));
      dispatch(clearState());
    }
    if (error) {
       toast.error(error, { position: "top-center", zIndex: 9999 });
       dispatch(clearState());
    }
  }, [success, error, dispatch, currentPage, showModal, filterTeacher, filterGrade]);

  const handlePageChange = (page) => {
    if (isLoading) return;
    if (page === currentPage) return;
    setCurrentPage(page);
  };

  const handleDelete = (tCode) => {
    setDeleting(tCode);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deleting) {
      dispatch(deleteTeachersCodes(deleting.id));
      setShowDeleteModal(false);
      setDeleting(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValues.teacher_id || !formValues.price || !formValues.grade_id || !formValues.file) {
      toast("من فضلك املأ كل البيانات");
      return;
    }
    const formData = new FormData();
    formData.append("teacher_id", formValues.teacher_id);
    formData.append("price", formValues.price);
    formData.append("grade_id", formValues.grade_id);
    formData.append("file", formValues.file);
    dispatch(importTeacherCodes(formData));
  };



  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 1040, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  if (isLoading && (!teachers_codes || !teachers_codes.data || teachers_codes.data.length === 0)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>
      <style>{`
        .tcode-table {
          width: 100%;
          border-collapse: collapse;
          text-align: right;
        }
        .tcode-table th {
          background-color: #f8fafc !important;
          color: #475569 !important;
          font-weight: 700 !important;
          font-size: 13px !important;
          padding: 14px 16px !important;
          border-bottom: 1px solid #e2e8f0 !important;
          text-align: center !important;
        }
        .tcode-table td {
          padding: 14px 16px !important;
          font-size: 14px !important;
          vertical-align: middle !important;
          border-bottom: 1px solid #f1f5f9 !important;
          text-align: center !important;
        }
        .tcode-custom-select {
          width: 100% !important;
          padding: 10px 14px !important;
          padding-left: 40px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
          background-repeat: no-repeat !important;
          background-position: left 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .tcode-custom-select:focus {
          border-color: var(--main-color) !important;
          box-shadow: 0 0 0 3px rgba(228, 109, 23, 0.1) !important;
        }
        .tcode-input {
          width: 100% !important;
          padding: 10px 14px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .tcode-input:focus {
          border-color: var(--main-color) !important;
          box-shadow: 0 0 0 3px rgba(228, 109, 23, 0.1) !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            🔑 أكواد المعلمين
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>توليد وإدارة وتصدير أكواد المعلمين للطلاب للوصول للحصص</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <PermissionMotionButton
            permission="create-teacher-codes"
            onClick={() => navigate("/add_teacher_code")}
            style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 18px", borderRadius: 10, border: "none",
              background: "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13 }}
          >
            <FaPlus size={12} /> إضافة كود جديد
          </PermissionMotionButton>
          <motion.button
            type="button"
            className="btn text-white d-flex align-items-center gap-2"
            style={{ borderRadius: "10px", background: "var(--green-color)", fontWeight: 700, padding: "9px 18px", border: "none", fontSize: 13 }}
            whileHover={!download ? { scale: 1.02 } : {}}
            whileTap={!download ? { scale: 0.98 } : {}}
            onClick={!download ? handleDownloadExcel : undefined}
            disabled={download}
          >
            <FaFileExcel />
            <span>{download ? "جارٍ التحميل..." : "تصدير النموذج"}</span>
          </motion.button>
          <motion.button
            type="button"
            className="btn text-white d-flex align-items-center gap-2"
            style={{ borderRadius: "10px", background: "#4f46e5", fontWeight: 700, padding: "9px 18px", border: "none", fontSize: 13 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowModal(true)}
          >
            <FaFileExcel />
            <span>رفع ملف</span>
          </motion.button>
        </div>
      </div>

      {/* ── Filter Bar ───────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "18px 22px", marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 10, textAlign: "right" }}>تصفية الأكواد</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 15, alignItems: "center" }}>
          <div style={{ minWidth: 200, flex: "1 1 auto" }}>
            <select
              className="tcode-custom-select"
              value={filterTeacher}
              onChange={(e) => {
                setFilterTeacher(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">كل المعلمين</option>
              {all_teachers?.map((t) => (
                <option key={t.id} value={t.id}>{t.name_ar}</option>
              ))}
            </select>
          </div>
          <div style={{ minWidth: 200, flex: "1 1 auto" }}>
            <select
              className="tcode-custom-select"
              value={filterGrade}
              onChange={(e) => {
                setFilterGrade(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">كل الصفوف</option>
              {settingsGrades?.map((g) => (
                <option key={g.id} value={g.id}>{g.name_ar}</option>
              ))}
            </select>
          </div>
          {(filterTeacher || filterGrade) && (
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => {
                setFilterTeacher("");
                setFilterGrade("");
                setCurrentPage(1);
              }}
              style={{ padding: "9px 16px", borderRadius: 9, fontSize: 13, fontWeight: 600 }}
            >
              إعادة تعيين
            </button>
          )}
        </div>
      </div>

      {/* ── Table Container ────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)", padding: 0 }}>
        {(!teachers_codes || !teachers_codes.data || teachers_codes.data.length === 0) ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
            <IoSchool size={48} className="text-muted mb-3" />
            <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15, color: "#64748b" }}>لا توجد أكواد معلمين مسجلة</p>
          </div>
        ) : (
          <div style={{ padding: 0 }}>
            <div className="table-responsive">
              <table className="tcode-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>الكود</th>
                    <th>اسم المعلم</th>
                    <th>المرحلة الدراسية</th>
                    <th>السعر</th>
                    <th>الحالة</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {teachers_codes.data.map((teacher_code, index) => (
                    <tr key={teacher_code.id}>
                      <td>
                        {meta ? (meta.current_page - 1) * meta.per_page + index + 1 : index + 1}
                      </td>
                      <td>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#0284c7", background: "#e0f2fe", border: "1px solid #bae6fd", padding: "4px 12px", borderRadius: 8, letterSpacing: "0.5px" }}>
                          {teacher_code.code}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600, color: "#0f172a" }}>
                        {teacher_code.teacher?.name_ar || "-"}
                      </td>
                      <td style={{ color: "#475569" }}>
                        {teacher_code.grade?.name_ar ||
                          teacher_code.grade_name_ar ||
                          (settingsGrades?.find((g) => String(g.id) === String(teacher_code.grade_id))?.name_ar) ||
                          "-"}
                      </td>
                      <td style={{ fontWeight: 700, color: "#16a34a" }}>
                        {teacher_code.price} ج.م
                      </td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 700, color: teacher_code.status === "active" ? "#15803d" : "#64748b", background: teacher_code.status === "active" ? "#d1fae5" : "#f1f5f9", padding: "3px 10px", borderRadius: 20 }}>
                          {teacher_code.status === "active" ? "مفعل" : "معطل"}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: "inline-flex", gap: 5, justifyContent: "center" }}>
                          <PermissionButton
                            onClick={() => {
                              sessionStorage.setItem(
                                `teacher_code_${teacher_code.id}`,
                                JSON.stringify({ id: teacher_code.id })
                              );
                              navigate(`/edit_teacher_code/${teacher_code.id}`);
                            }}
                            permission="edit-teacher-codes"
                            title="تعديل"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid var(--warning-color)", background: "#fefce8", color: "var(--warning-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaEdit size={14} />
                          </PermissionButton>
                          <PermissionButton
                            onClick={() => handleDelete(teacher_code)}
                            permission="delete-teacher-codes"
                            title="حذف"
                            style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid #fecaca", background: "#fef2f2", color: "var(--red-color)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          >
                            <FaTrash size={14} />
                          </PermissionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "0 16px 20px" }}>
              <Pagination meta={meta} currentPage={currentPage} loading={isLoading} onChange={handlePageChange} />
            </div>
          </div>
        )}
      </div>

      {/* ── Import Modal ──────────────────────────────── */}
      {showModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 500, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 800, fontSize: 17, color: "#0f172a" }}>📂 رفع ملف الأكواد</span>
              <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: "20px 24px" }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 6 }}>المعلم *</label>
                <select
                  className="tcode-custom-select"
                  value={formValues.teacher_id}
                  onChange={(e) => setFormValues((prev) => ({ ...prev, teacher_id: e.target.value }))}
                  required
                >
                  <option value="">اختر معلم</option>
                  {all_teachers?.map((t) => (
                    <option key={t.id} value={t.id}>{t.name_ar}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 6 }}>المرحلة الدراسية *</label>
                <select
                  className="tcode-custom-select"
                  value={formValues.grade_id}
                  onChange={(e) => setFormValues((prev) => ({ ...prev, grade_id: e.target.value }))}
                  required
                >
                  <option value="">اختر مرحلة دراسية</option>
                  {settingsGrades?.map((g) => (
                    <option key={g.id} value={g.id}>{g.name_ar}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 6 }}>السعر *</label>
                <input
                  type="number"
                  className="tcode-input"
                  value={formValues.price}
                  onChange={(e) => setFormValues((prev) => ({ ...prev, price: e.target.value }))}
                  required
                  min="0"
                  placeholder="أدخل السعر..."
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#64748b", display: "block", marginBottom: 6 }}>الملف Excel (.xls, .xlsx) *</label>
                <input
                  type="file"
                  accept=".xls,.xlsx"
                  className="tcode-input"
                  onChange={(e) => setFormValues((prev) => ({ ...prev, file: e.target.files[0] }))}
                  required
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid #f1f5f9", paddingTop: 16 }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
                <button type="submit" disabled={isLoading} style={{ padding: "8px 28px", borderRadius: 9, border: "none", background: "linear-gradient(135deg,var(--main-color),var(--main-light))", color: "#fff", fontWeight: 700, cursor: "pointer" }}>
                  {isLoading ? "جارٍ الرفع..." : "رفع الملف"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Modal ──────────────────────────────── */}
      {showDeleteModal && (
        <div style={overlay}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 25px 60px rgba(0,0,0,.25)", overflow: "hidden" }}>
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 17, display: "flex", alignItems: "center", gap: 6 }}><FaTrash /> تأكيد الحذف</span>
              <button onClick={() => { setShowDeleteModal(false); setDeleting(null); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
            </div>
            <div style={{ padding: "18px 24px" }}>
              <p style={{ margin: 0, color: "#334155" }}>هل أنت متأكد من حذف كود المعلم هذا؟</p>
              <div style={{ marginTop: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#64748b", borderRight: "3px solid var(--red-color)" }}>
                🔑 {deleting?.code}
              </div>
              <p style={{ margin: "10px 0 0", color: "var(--red-color)", fontSize: 13 }}>⚠ هذا الإجراء لا يمكن التراجع عنه.</p>
            </div>
            <div style={{ padding: "14px 24px", background: "#f8fafc", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => { setShowDeleteModal(false); setDeleting(null); }} style={{ padding: "8px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer" }}>إلغاء</button>
              <button onClick={confirmDelete} disabled={isLoading} style={{ padding: "8px 20px", borderRadius: 9, border: "none", background: "var(--red-color)", color: "#fff", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                {isLoading ? <><span className="spinner-border spinner-border-sm"></span> جاري الحذف...</> : "حذف الكود"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherCodes;
